import type { CSSProperties } from "react";
import { cn } from "../lib/cn";

/* ----- Decorative, CSP-safe kid illustrations (moved verbatim from Landing) ----- */

export function Balloon({ className = "", fill, style }: { className?: string; fill: string; style?: CSSProperties }) {
  return (
    <svg viewBox="0 0 44 60" className={className} style={style} aria-hidden="true">
      <ellipse cx="22" cy="22" rx="20" ry="24" fill={fill} />
      <ellipse cx="15" cy="15" rx="4" ry="7" fill="#ffffff" opacity="0.4" />
      <path d="M22 46 q5 7 -2 14" stroke={fill} strokeWidth="2" fill="none" opacity="0.5" />
    </svg>
  );
}

export function Cloud({ className = "", style }: { className?: string; style?: CSSProperties }) {
  return (
    <svg viewBox="0 0 80 40" className={className} style={style} aria-hidden="true" fill="#ffffff">
      <circle cx="26" cy="24" r="14" />
      <circle cx="46" cy="18" r="18" />
      <circle cx="62" cy="24" r="12" />
      <rect x="22" y="26" width="46" height="14" rx="7" />
    </svg>
  );
}

export function Sun({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 80 80" className={className} aria-hidden="true">
      <circle cx="40" cy="40" r="18" fill="#fbbf24" />
      <g stroke="#fbbf24" strokeWidth="4" strokeLinecap="round">
        <line x1="40" y1="6" x2="40" y2="16" />
        <line x1="40" y1="64" x2="40" y2="74" />
        <line x1="6" y1="40" x2="16" y2="40" />
        <line x1="64" y1="40" x2="74" y2="40" />
        <line x1="16" y1="16" x2="23" y2="23" />
        <line x1="57" y1="57" x2="64" y2="64" />
        <line x1="16" y1="64" x2="23" y2="57" />
        <line x1="57" y1="23" x2="64" y2="16" />
      </g>
    </svg>
  );
}

/* ----- Playful shapes — the Phase 1 composition vocabulary ----- */

/** Concentric pastel arcs (rose / amber / emerald / sky). */
export function Rainbow({ className = "", style }: { className?: string; style?: CSSProperties }) {
  return (
    <svg
      viewBox="0 0 120 64"
      className={cn("pointer-events-none", className)}
      style={style}
      fill="none"
      aria-hidden="true"
    >
      <g strokeWidth="8" strokeLinecap="round">
        <path d="M12 60 A48 48 0 0 1 108 60" stroke="#fda4af" />
        <path d="M22 60 A38 38 0 0 1 98 60" stroke="#fcd34d" />
        <path d="M32 60 A28 28 0 0 1 88 60" stroke="#6ee7b7" />
        <path d="M42 60 A18 18 0 0 1 78 60" stroke="#7dd3fc" />
      </g>
    </svg>
  );
}

/** A four-point twinkle. */
export function Sparkle({
  className = "",
  style,
  fill = "currentColor",
}: {
  className?: string;
  style?: CSSProperties;
  fill?: string;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={cn("pointer-events-none", className)}
      style={style}
      fill={fill}
      aria-hidden="true"
    >
      <path d="M12 0C13 7 17 11 24 12C17 13 13 17 12 24C11 17 7 13 0 12C7 11 11 7 12 0Z" />
    </svg>
  );
}

/** A five-point star. */
export function Star({
  className = "",
  style,
  fill = "currentColor",
}: {
  className?: string;
  style?: CSSProperties;
  fill?: string;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={cn("pointer-events-none", className)}
      style={style}
      fill={fill}
      aria-hidden="true"
    >
      <path d="M12 1 L14.64 8.36 L22.46 8.6 L16.28 13.39 L18.47 20.9 L12 16.5 L5.53 20.9 L7.72 13.39 L1.54 8.6 L9.36 8.36 Z" />
    </svg>
  );
}

/** One organic blob, for soft background accents. */
export function Blob({
  className = "",
  style,
  fill = "currentColor",
}: {
  className?: string;
  style?: CSSProperties;
  fill?: string;
}) {
  return (
    <svg
      viewBox="0 0 200 200"
      className={cn("pointer-events-none", className)}
      style={style}
      fill={fill}
      aria-hidden="true"
    >
      <path d="M100 22C128 22 152 38 163 64C173 88 168 117 150 138C133 158 106 170 80 164C52 158 30 137 24 109C18 82 30 53 54 36C68 27 84 22 100 22Z" />
    </svg>
  );
}

/** Full-width single-wave band divider; set `flip` to mirror it vertically. */
export function WaveDivider({
  className = "",
  fill = "currentColor",
  flip = false,
}: {
  className?: string;
  fill?: string;
  flip?: boolean;
}) {
  return (
    <svg
      viewBox="0 0 1440 80"
      preserveAspectRatio="none"
      className={cn("pointer-events-none block w-full", flip && "-scale-y-100", className)}
      aria-hidden="true"
    >
      <path d="M0 40C240 0 480 0 720 40C960 80 1200 80 1440 40L1440 80L0 80Z" fill={fill} />
    </svg>
  );
}
